import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { changesApi, rfisApi, projectsApi, lookupsApi, usersApi, type ChangeOrder } from '@/lib/apiClient';
import { StateView, Button, Combobox } from '@/components';
import { DATE_INPUT_MIN, fmtDate } from '@/lib/dates';
import { easternTodayYmd } from '@/modules/manpower/lib/gridDates';
import { isFieldLeaderOnly } from '@/lib/access';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { SageSyncPanel } from '@/modules/sage/SageSyncPanel';
import {
  SAGE_MATCH_TOLERANCE_DOLLARS, acceptedBeforeSent, attentionLine, avgDaysToAccept, canTakeSageAmount, dayCount, defaultSagePick,
  nudgeSummary, stageDateError, stageOf, stageView, suggestedSageMatches, type StageTone,
} from './lib/changeStage';

// Change Management (CMS expansion): PCO / COR / OCO tracking. The product owner's scoping
// call: NOT integrated with Cost Tracker / Intacct — accepted changes must be
// entered in Intacct by the PM/PE, and this tab enforces that with an
// explicit "link to the Intacct change order" confirmation + a visible queue.
// Revised contract is derived here for display only. The answered-RFI gate
// forces a change-order review on every answered RFI before it can be
// considered done.
//
// A change is ONE record that moves through stages, each a button that stamps who
// and when: a PCO (potential) is marked sent to the client (COR), then accepted
// by the client (OCO), then entered in Intacct and linked. Declined or withdrawn
// ends it. The stage comes from the API (`stage`), derived from status.

/** What a new change can be opened as. Nearly every change starts as a PCO. */
const OPEN_AS: Array<{ value: 'PCO' | 'COR' | 'OCO'; label: string }> = [
  { value: 'PCO', label: 'PCO — a potential change (most start here)' },
  { value: 'COR', label: 'COR — already sent to the client' },
  { value: 'OCO', label: 'OCO — the client already accepted it' },
];

const STAGE_TONE: Record<StageTone, { bg: string; fg: string; dot: string }> = {
  open:     { bg: 'var(--ew-performance-50)', fg: 'var(--ew-performance-700)', dot: 'var(--ew-performance)' },
  client:   { bg: 'var(--ew-quality-50)',     fg: 'var(--ew-quality-700)',     dot: 'var(--ew-quality)' },
  accepted: { bg: 'var(--ew-green-50)',       fg: 'var(--ew-green-700)',       dot: 'var(--ew-green)' },
  ended:    { bg: 'var(--ew-n-100)',          fg: 'var(--ew-n-500)',           dot: 'var(--ew-n-400)' },
};

type StageActionKind = 'send' | 'accept' | 'decline' | 'correct' | 'refresh';
interface StageAction { kind: StageActionKind; change: ChangeOrder }

/** One summary tile. `onToggle` makes it a button (the filter tile); the rest are plain figures. */
interface Kpi {
  label: string;
  value: string;
  sub: string;
  accent: string;
  onToggle?: () => void;
  pressed?: boolean;
}

function money(v: number | null | undefined): string {
  if (v == null) return '—';
  const sign = v < 0 ? '-' : '+';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
function moneyPlain(v: number): string {
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const css = `
  .chg-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .chg-table th {
    text-align: left; padding: 10px var(--ew-s-4); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100);
  }
  .chg-table td { padding: 11px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .chg-table tr:last-child td { border-bottom: 0; }
  .chg-muted { color: var(--ew-n-500); }
  .chg-badge {
    display: inline-flex; align-items: center; gap: 6px; border-radius: var(--ew-r-pill);
    padding: 3px 10px; font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); white-space: nowrap;
  }
  .chg-code { font-family: var(--ew-font-mono, ui-monospace, monospace); font-size: 12px; background: var(--ew-n-100); color: var(--ew-n-600); border-radius: 4px; padding: 2px 6px; white-space: nowrap; }
  .chg-kpi--toggle { cursor: pointer; }
  .chg-kpi--toggle:hover { border-color: var(--ew-n-400); }
  .chg-kpi--toggle:focus-visible { outline: 2px solid var(--ew-safety); outline-offset: 2px; }
  .chg-kpi--toggle[aria-pressed='true'] { border-color: var(--ew-safety); box-shadow: 0 0 0 1px var(--ew-safety) inset; }
  .chg-kpi { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: var(--ew-s-4); position: relative; overflow: hidden; display: flex; flex-direction: column; gap: 4px; }
`;

export default function ChangesTab({ projectId, locked = false }: { projectId: number; locked?: boolean }) {
  const qc = useQueryClient();

  const query = useQuery({ queryKey: ['changes', projectId], queryFn: () => changesApi.list(projectId) });
  const rfiQuery = useQuery<any[]>({ queryKey: ['rfis', projectId], queryFn: () => rfisApi.list({ projectId }) as Promise<any[]>, staleTime: 60_000 });
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{
      contractValue: number | null;
      sageContract?: {
        contractId: string | null; original: number | null; approvedChange: number | null;
        pendingChange: number | null; revised: number | null; billed: number | null; syncedAt: string | null;
      } | null;
    }>,
    staleTime: 60_000,
  });

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  // Activity-locked (SYSTAC / ClosedWon) projects can't take change orders
  // until a Sage job number lands — read-only regardless of permission.
  const canEdit = ((me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false)) && !locked;
  /**
   * ⚠️ Covers BOTH the contract figure (PR #290) and change-order amounts
   * (product owner's follow-up) — one flag because the rule is the same today, and the
   * API strips both sets of fields for the same users. If those two policies
   * ever diverge, split this; the API already keeps them as separate named
   * predicates (`canSeeContractValue` / `canSeeCoAmount`) for that reason.
   *
   * This is presentation only. Nothing here is a gate — the figures are already
   * absent from the responses by the time they reach this component.
   */
  const hideMoney = isFieldLeaderOnly(me);

  const [drawer, setDrawer] = useState<{ rfi?: any } | null>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['changes', projectId] });
    qc.invalidateQueries({ queryKey: ['rfis', projectId] });
    qc.invalidateQueries({ queryKey: ['project-counts', projectId] });
    // The Today page lists a change that has sat too long; acting on it must clear that row now,
    // not when its 5-minute cache lapses.
    qc.invalidateQueries({ queryKey: ['today'] });
    // A change moving moves its crew requests (#303 slice 5): linked to an
    // Intacct CO -> its pco requests are now co; rejected -> they are declined
    // and it may no longer be requested against. The request form's cached
    // context (30s staleTime) and the inbox must not keep saying otherwise
    // (review of #442).
    qc.invalidateQueries({ queryKey: ['manpower', 'request-context', projectId] });
    qc.invalidateQueries({ queryKey: ['manpower', 'requests'] });
    qc.invalidateQueries({ queryKey: ['manpower', 'request-open-count'] });
  };

  const changes = query.data ?? [];
  // Changes the API flagged as having sat too long. The rule (and its threshold) is the API's, shared
  // with the Today page; this only counts and filters.
  const nudge = nudgeSummary(changes);
  const [nudgedOnly, setNudgedOnly] = useState(false);
  // On only while there is something to show: acting on the last flagged change (or the page being
  // read by someone who cannot act) must never leave a filter holding an empty table.
  const onlyNudged = nudgedOnly && nudge.count > 0;
  useEffect(() => {
    // Not while the first read is in flight: no rows yet is not "nothing flagged".
    if (query.isSuccess && nudge.count === 0) setNudgedOnly(false);
  }, [query.isSuccess, nudge.count]);
  // A filter chosen on one job is not meant for the next one.
  useEffect(() => { setNudgedOnly(false); }, [projectId]);
  const approved = changes.filter((c) => c.status === 'approved');
  const pending = changes.filter((c) => c.status === 'pending');
  // The PCOs that have gone out: pending, and past the potential stage.
  const withClient = pending.filter((c) => stageOf(c) === 'COR');
  const toEnter = approved.filter((c) => !c.intacctEnteredAt);
  // Intacct is authoritative for contract value (synced from PROJECTCONTRACT);
  // PMT's own tracked figures are the fallback + the reconciliation subject.
  const sc = projectQuery.data?.sageContract ?? null;
  const base = sc?.original ?? projectQuery.data?.contractValue ?? null;
  const approvedTotal = approved.reduce((s, c) => s + (c.amount ?? 0), 0);
  const pendingTotal = pending.reduce((s, c) => s + (c.amount ?? 0), 0);
  const revisedContract = sc?.revised ?? (base != null ? base + approvedTotal : null);
  // Reconciliation — the ACTIONABLE signal is an accepted CO not yet VERIFIED
  // against a real Intacct CO: either never linked (still "enter it"), or the
  // linked CO wasn't found on the last sync (broken link). Both flag red.
  // Cumulative PMT-vs-Intacct totals are NOT compared for drift (pre-PMT
  // Intacct history makes them legitimately differ) — shown only as reference.
  const broken = approved.filter((c) => c.intacctEnteredAt && c.intacctCoFound === false);
  const unverified = [...toEnter, ...broken];
  const unverifiedTotal = unverified.reduce((s, c) => s + (c.amount ?? 0), 0);
  // ⚠️ The second disjunct opens the "Reference" branch, which is NOTHING BUT
  // two dollar figures — so it must not open for a field leader. It happens to
  // be unreachable for them already (`sageContract` is nulled by the API since
  // PR #290), but leaning on that would put this panel's correctness in another
  // module's hands.
  const recon = (unverified.length > 0 || (!hideMoney && sc?.approvedChange != null))
    ? {
        red: unverified.length > 0,
        count: unverified.length,
        total: unverifiedTotal,
        pmt: approvedTotal,
        intacct: sc?.approvedChange ?? null,
      }
    : null;
  // The days the CLIENT took to accept, sent to accepted: see avgDaysToAccept for why it is neither opened to decided nor counts a decline.
  const avgReviewDays = useMemo(() => avgDaysToAccept(changes), [changes]);

  // The gate: answered RFIs with no change-review disposition yet.
  const unresolvedRfis = (rfiQuery.data ?? []).filter((r) => r.status === 'answered' && !r.coReview);

  const noImpactMut = useMutation({
    mutationFn: (rfiId: number) => changesApi.rfiNoImpact(rfiId),
    onSuccess: invalidate,
  });
  const [enterFor, setEnterFor] = useState<ChangeOrder | null>(null);
  // Sent to client / client accepted / declined / correct the date / take Sage's amount.
  // Each runs inside its dialog and reports back through onDone, which invalidates.
  const [stageAction, setStageAction] = useState<StageAction | null>(null);

  // ⚠️ Field leaders get no contract figure (product owner, 2026-08-03). The API already
  // nulls `sageContract`/`contractValue` for them, so this tile would render
  // "Revised Contract —"; drop it rather than advertise the absence. The Changes
  // tab is NOT in OFFICE_TABS, so field leaders do reach this screen.
  const kpis: Kpi[] = [
    ...(hideMoney ? [] : [
      { label: 'Revised Contract', value: revisedContract != null ? moneyPlain(revisedContract) : '—', sub: sc?.revised != null ? `Intacct · orig ${moneyPlain(sc.original ?? 0)} + chg ${moneyPlain(sc.approvedChange ?? 0)}` : base != null ? `Base ${moneyPlain(base)} + ${moneyPlain(approvedTotal)} accepted in the Project Tracker` : 'No contract value', accent: 'var(--ew-green)' },
    ]),
    // ⚠️ The COUNTS stay — a field leader should know their job has change
    // orders and how many are pending. Only the money clause goes, and the
    // amount-free tail ("avg Nd to decide") is preserved rather than dropped
    // with it: it's schedule information, not cost.
    {
      label: 'Accepted COs',
      value: String(approved.length),
      sub: hideMoney
        ? `${approved.length === 1 ? 'change' : 'changes'} added to the contract`
        : `${money(approvedTotal) === '—' ? '$0' : money(approvedTotal)} to contract`,
      accent: 'var(--ew-quality)',
    },
    {
      label: 'Open (PCO + COR)',
      value: String(pending.length),
      sub: hideMoney
        ? `${withClient.length} with the client${avgReviewDays != null ? ` · avg ${avgReviewDays}d to accept` : ''}`
        : `${money(pendingTotal) === '—' ? '$0' : money(pendingTotal)} exposure · ${withClient.length} with the client${avgReviewDays != null ? ` · avg ${avgReviewDays}d to accept` : ''}`,
      accent: 'var(--ew-performance)',
    },
    { label: 'Enter in Intacct', value: String(toEnter.length), sub: toEnter.length ? 'Accepted — awaiting Intacct entry' : 'All accepted changes entered', accent: 'var(--ew-safety)' },
    // Only for someone who can act on it; the API sends a field leader no cue at all. A button while
    // there is something to show (or the filter is on, so it can be turned off), otherwise a plain figure.
    ...(canEdit ? [{
      label: 'Needs attention',
      value: String(nudge.count),
      sub: nudge.count
        ? `Longest ${dayCount(nudge.longest)} ·${onlyNudged ? 'showing only these' : 'click to filter'}`
        : 'Nothing has sat too long',
      accent: 'var(--ew-safety)',
      ...(nudge.count > 0 ? { onToggle: () => setNudgedOnly((v) => !v), pressed: onlyNudged } : {}),
    }] : []),
  ];

  return (
    <>
      <style>{css}</style>

      {/* ── Honest workflow banner: tracking here, entry in Intacct by hand. ── */}
      <div style={{ background: '#fff', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '12px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ font: 'var(--ew-fw-semibold) 11px/1 var(--ew-font)', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ew-performance)' }}>◇ Tracked in the Project Tracker · Entered in Intacct by the PM/PE</span>
        <span style={{ font: 'var(--ew-fw-regular) 12px var(--ew-font)', color: 'var(--ew-n-500)' }}>
          Open a PCO, mark it sent when it goes to the client (COR), and mark it accepted when they say yes (OCO).
          Then the PM/PE enters it in Sage Intacct and links it here — nothing posts automatically.
        </span>
      </div>

      {/* ── Read-only Intacct sync freshness (verification depends on it). The
          "Sync now" control lives in Admin › Integrations. showMatching=false:
          PO-line matching detail is irrelevant here — only freshness matters. ── */}
      <SageSyncPanel variant="status" showMatching={false} />

      {/* ── Reconciliation with Intacct (verification, not integration): PMT's
          tracked accepted COs vs Intacct's approved change $. ── */}
      {recon ? (() => {
        const tone = recon.red
          ? { bg: 'var(--ew-danger-50)', bd: 'var(--ew-danger-100)', fg: 'var(--ew-danger-700)' }
          : { bg: 'var(--ew-quality-50)', bd: 'var(--ew-quality-100)', fg: 'var(--ew-quality-700)' };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px var(--ew-s-4)', marginBottom: 'var(--ew-s-4)', borderRadius: 'var(--ew-r-md)', background: tone.bg, border: `1px solid ${tone.bd}` }}>
            {recon.red ? (
              <>
                <span style={{ fontWeight: 700, color: tone.fg }}>
                  ⚠ {recon.count} accepted change{recon.count === 1 ? '' : 's'} not verified in Intacct
                </span>
                {/* The COUNT is the signal and survives; the dollar total does
                    not. The "enter it in Intacct" instruction goes too for a
                    field leader — it's a PM action they can't perform. */}
                <span style={{ fontSize: 12.5, color: 'var(--ew-n-600)' }}>
                  {hideMoney
                    ? 'Not yet linked to a live Sage Intacct CO.'
                    : <><b>{moneyPlain(recon.total)}</b> accepted in the Project Tracker and not linked to a live Sage Intacct CO — enter it in Intacct, then confirm by picking the CO here.</>}
                </span>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 700, color: tone.fg }}>◇ Reference</span>
                <span style={{ fontSize: 12.5, color: 'var(--ew-n-600)' }}>
                  The Project Tracker has <b>{moneyPlain(recon.pmt)}</b> in accepted COs · Intacct's approved-change total for this contract is <b>{recon.intacct != null ? moneyPlain(recon.intacct) : '—'}</b>.
                </span>
              </>
            )}
            {/* Sync freshness lives in the single "Synced from Sage Intacct"
                line above — no second (and potentially divergent) timestamp here. */}
          </div>
        );
      })() : null}

      {/* ── KPI tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--ew-s-3)', marginBottom: 'var(--ew-s-4)' }}>
        {kpis.map((k) => (
          <div
            key={k.label}
            className={`chg-kpi${k.onToggle ? ' chg-kpi--toggle' : ''}`}
            role={k.onToggle ? 'button' : undefined}
            tabIndex={k.onToggle ? 0 : undefined}
            aria-pressed={k.onToggle ? k.pressed : undefined}
            onClick={k.onToggle}
            onKeyDown={k.onToggle ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); k.onToggle!(); } } : undefined}
          >
            <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: k.accent }} />
            <span style={{ font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' }}>{k.label}</span>
            <span style={{ font: 'var(--ew-fw-semibold) 22px/1.1 var(--ew-font)', fontVariantNumeric: 'tabular-nums' }}>{k.value}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ew-n-400)' }}>{k.sub}</span>
          </div>
        ))}
      </div>

      {/* ── Answered-RFI review gate ── */}
      {unresolvedRfis.length > 0 ? (
        <div className="pw-card" style={{ marginBottom: 'var(--ew-s-4)', borderLeft: '3px solid var(--ew-safety)' }}>
          <div style={{ padding: '14px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
            <span style={{ font: 'var(--ew-fw-semibold) 14px var(--ew-font)' }}>⚠ Answered RFIs — change-order review required</span>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginTop: 3 }}>Every answered RFI must be reviewed for cost/schedule impact before it closes.</div>
          </div>
          {unresolvedRfis.map((r) => (
            <div key={r.id} style={{ padding: '12px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
              <div style={{ fontSize: 13 }}>
                <b>{r.rfiNumber}</b> <span className="chg-badge" style={{ background: 'var(--ew-green-50)', color: 'var(--ew-green-700)', marginLeft: 6 }}>Answered</span>
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>{r.subject ?? r.question}</div>
              {r.response ? <div className="chg-muted" style={{ fontSize: 12.5, marginTop: 3 }}>Answer: {r.response}</div> : null}
              {canEdit ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>Does this answer require a change order?</span>
                  <Button size="sm" onClick={() => setDrawer({ rfi: r })}>Yes — create PCO</Button>
                  <Button variant="secondary" size="sm" onClick={() => noImpactMut.mutate(r.id)} isLoading={noImpactMut.isPending}>No cost impact</Button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* ── Change orders table ── */}
      <div className="pw-card">
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px var(--ew-s-4)', borderBottom: '1px solid var(--ew-n-100)' }}>
          <h3 style={{ font: 'var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', margin: 0 }}>Change Orders</h3>
          {onlyNudged ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--ew-safety-700)' }}>
              Showing only changes that need attention ·{' '}
              <button type="button" onClick={() => setNudgedOnly(false)} style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Show all</button>
            </span>
          ) : null}
          {canEdit ? <span style={{ marginLeft: 'auto' }}><Button size="sm" onClick={() => setDrawer({})}>+ New PCO</Button></span> : null}
        </div>
        <StateView query={query} empty="No change orders yet — create the first PCO, or let the RFI review gate feed them.">
          {(rows: ChangeOrder[]) => rows.length === 0 ? (
            <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', fontSize: 13 }}>No change orders yet — create the first PCO, or let the RFI review gate feed them.</div>
          ) : (
            <div className="ew-table-scroll">
              <table className="chg-table ew-cards">
                <thead>
                  <tr>
                    <th>Change #</th>
                    <th>Description</th>
                    <th>Stage</th>
                    <th>Cost Code</th>
                    {/* Column dropped rather than shown full of em-dashes —
                        same call as the Contract chip: a column of "—" still
                        advertises a figure they aren't meant to know exists. */}
                    {!hideMoney && <th style={{ textAlign: 'right' }}>Amount</th>}
                    <th>Days</th>
                    {canEdit ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {(onlyNudged ? rows.filter((c) => c.attention) : rows).map((c) => {
                    const stage = stageOf(c);
                    // Only where the buttons are: canEdit is false on a locked (SYSTAC- / ClosedWon-) job, where a
                    // cue to send or link would tell the PM to do what the lifecycle forbids.
                    const nudgeText = canEdit ? attentionLine(c) : null;
                    const sv = stageView(c);
                    const tone = STAGE_TONE[sv.tone];
                    return (
                      <tr key={c.id}>
                        <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.changeNo}</td>
                        <td data-label="Description">
                          {c.description}
                          {c.rfiNumber ? <div className="chg-muted" style={{ fontSize: 11.5, marginTop: 2 }}>from {c.rfiNumber}</div> : null}
                          {c.intacctEnteredAt ? (
                            c.intacctCoFound === false ? (
                              <div style={{ fontSize: 11.5, color: 'var(--ew-danger-700)', marginTop: 2 }}>
                                ⚠ Linked Intacct CO {c.intacctRef} not found on last sync — re-link
                              </div>
                            ) : (
                              <>
                                <div style={{ fontSize: 11.5, color: 'var(--ew-green-700)', marginTop: 2 }}>
                                  ✓ Intacct {c.intacctRef} · entered {fmtDate(c.intacctEnteredAt)}{c.intacctEnteredBy ? ` by ${c.intacctEnteredBy}` : ''}
                                </div>
                                {/* Both figures are null for a field leader, so
                                    this never fires for them — deliberate: the
                                    drift warning is a PM reconciliation action,
                                    and it cannot be stated without the numbers.
                                    The amount-free "not found on last sync"
                                    warning above still shows. */}
                                {c.intacctCoAmount != null && c.amount != null && Math.abs(c.intacctCoAmount - c.amount) >= 1 ? (
                                  <div style={{ fontSize: 11.5, color: 'var(--ew-safety-700)', marginTop: 2 }}>
                                    ⚠ amount differs — the Project Tracker has {money(c.amount)}, Intacct has {money(c.intacctCoAmount)}
                                  </div>
                                ) : null}
                                {/* The frozen amount, and a flag when Intacct has moved
                                    off it. Both are null for a field leader (the API
                                    redacts them), so neither line can show for them. */}
                                {c.countedAmount != null ? (
                                  <div className="chg-muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                                    Counting {money(c.countedAmount)} (Intacct's amount when linked)
                                  </div>
                                ) : null}
                                {/* A linked change with nothing frozen (linked before the stage
                                    columns existed, or while Intacct showed no total) gets the
                                    "Take Sage's amount" button below, and NO warning here: every
                                    change linked before this shipped looks the same, and it is
                                    the count from go-live that matters, so a warning on each
                                    would be noise. */}
                                {c.countedAmountDiffers && c.countedAmount != null && c.intacctCoAmount != null ? (
                                  <div style={{ fontSize: 11.5, color: 'var(--ew-safety-700)', marginTop: 2 }}>
                                    ⚠ Intacct now shows {money(c.intacctCoAmount)}; this change still counts {money(c.countedAmount)}
                                  </div>
                                ) : null}
                              </>
                            )
                          ) : c.status === 'approved' ? (
                            <div style={{ fontSize: 11.5, color: 'var(--ew-safety-700)', marginTop: 2 }}>⚠ Awaiting Intacct entry</div>
                          ) : null}
                        </td>
                        <td data-label="Stage">
                          <span className="chg-badge" style={{ background: tone.bg, color: tone.fg }}>
                            <span style={{ width: 6, height: 6, borderRadius: 999, background: tone.dot }} />
                            {sv.label}
                          </span>
                          {sv.detail ? <div className="chg-muted" style={{ fontSize: 11.5, marginTop: 3 }}>{sv.detail}</div> : null}
                          {nudgeText ? <div style={{ fontSize: 11.5, color: 'var(--ew-safety-700)', marginTop: 3 }}>⚑ {nudgeText}</div> : null}
                        </td>
                        <td>{c.costCode ? <span className="chg-code">{c.costCode}</span> : <span className="chg-muted">—</span>}</td>
                        {!hideMoney && (
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: c.status === 'rejected' ? 'var(--ew-n-400)' : c.amount != null && c.amount < 0 ? 'var(--ew-danger-700)' : 'var(--ew-dark)', whiteSpace: 'nowrap' }}>{money(c.amount)}</td>
                        )}
                        <td className="chg-muted" style={{ whiteSpace: 'nowrap' }}>{c.scheduleDays != null ? `${c.scheduleDays > 0 ? '+' : ''}${c.scheduleDays}d` : '—'}</td>
                        {canEdit ? (
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {stage === 'PCO' ? (
                              <span style={{ display: 'inline-flex', gap: 6 }}>
                                <Button size="sm" onClick={() => setStageAction({ kind: 'send', change: c })}>Sent to client…</Button>
                                {/* The client can say yes before the paperwork goes out; the API allows it and leaves the sent date blank. */}
                                <Button variant="secondary" size="sm" onClick={() => setStageAction({ kind: 'accept', change: c })}>Client accepted…</Button>
                                <Button variant="secondary" size="sm" onClick={() => setStageAction({ kind: 'decline', change: c })}>Withdraw…</Button>
                              </span>
                            ) : stage === 'COR' ? (
                              <span style={{ display: 'inline-flex', gap: 6 }}>
                                <Button size="sm" onClick={() => setStageAction({ kind: 'accept', change: c })}>Client accepted…</Button>
                                <Button variant="danger" size="sm" onClick={() => setStageAction({ kind: 'decline', change: c })}>Declined…</Button>
                              </span>
                            ) : stage === 'OCO' && !c.intacctEnteredAt ? (
                              <span style={{ display: 'inline-flex', gap: 6 }}>
                                <Button size="sm" onClick={() => setEnterFor(c)}>Link to Sage…</Button>
                                <Button variant="ghost" size="sm" onClick={() => setStageAction({ kind: 'correct', change: c })}>Correct date…</Button>
                              </span>
                            ) : stage === 'OCO' && c.intacctCoFound === false ? (
                              <Button variant="secondary" size="sm" onClick={() => setEnterFor(c)}>Re-link…</Button>
                            ) : canTakeSageAmount(c) ? (
                              <Button variant="secondary" size="sm" onClick={() => setStageAction({ kind: 'refresh', change: c })}>Take Sage's amount…</Button>
                            ) : null}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </StateView>
      </div>

      {drawer ? (
        <NewChangeDrawer
          projectId={projectId}
          rfi={drawer.rfi ?? null}
          onClose={() => setDrawer(null)}
          onDone={() => { setDrawer(null); invalidate(); }}
        />
      ) : null}

      {enterFor ? (
        <IntacctEnteredModal
          change={enterFor}
          projectId={projectId}
          onClose={() => setEnterFor(null)}
          onDone={() => { setEnterFor(null); invalidate(); }}
        />
      ) : null}

      {stageAction ? (
        <StageActionModal
          action={stageAction}
          onClose={() => setStageAction(null)}
          onDone={() => { setStageAction(null); invalidate(); }}
        />
      ) : null}
    </>
  );
}

// ─── New change — right-side drawer (mock: 500px). From the RFI gate it
//     arrives pre-filled with the RFI reference. ───────────────────────────────
function NewChangeDrawer({ projectId, rfi, onClose, onDone }: {
  projectId: number;
  rfi: any | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(drawerRef, { onEscape: onClose });
  const { data: costCodes } = useQuery({ queryKey: ['cost-codes'], queryFn: () => lookupsApi.listCostCodes(), staleTime: 5 * 60 * 1000 });

  const [type, setType] = useState('PCO');
  // Only used when the change is opened past the PCO stage (it arrived already sent
  // to the client, or already accepted). A PCO has neither.
  const [sentOn, setSentOn] = useState(easternTodayYmd());
  const [acceptedOn, setAcceptedOn] = useState(easternTodayYmd());
  const [clientRef, setClientRef] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState(rfi ? `${rfi.subject ?? rfi.question ?? ''}`.slice(0, 480) : '');
  const [costCode, setCostCode] = useState('');
  const [scheduleDays, setScheduleDays] = useState('');
  const [notes, setNotes] = useState(rfi ? `Origin: ${rfi.rfiNumber} — ${rfi.question ?? ''}\nAnswer: ${rfi.response ?? ''}` : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const codeOptions = useMemo(
    () => (costCodes ?? []).map((c: any) => ({ value: c.code, sub: c.name ?? undefined })),
    [costCodes],
  );

  const submit = async () => {
    if (!description.trim()) { setError('Description is required.'); return; }
    if (type === 'COR') {
      const bad = stageDateError(sentOn);
      if (bad) { setError(`Date sent: ${bad}`); return; }
    }
    if (type === 'OCO') {
      const bad = stageDateError(acceptedOn);
      if (bad) { setError(`Date accepted: ${bad}`); return; }
    }
    setBusy(true); setError(null);
    try {
      await changesApi.create({
        projectId,
        type,
        description: description.trim(),
        costCode: costCode.trim() || undefined,
        amount: amount.trim() === '' ? undefined : Number(amount),
        scheduleDays: scheduleDays.trim() === '' ? undefined : Number(scheduleDays),
        notes: notes.trim() || undefined,
        rfiId: rfi?.id,
        sentOn: type === 'COR' ? sentOn : undefined,
        acceptedOn: type === 'OCO' ? acceptedOn : undefined,
        clientRef: type === 'OCO' ? (clientRef.trim() || undefined) : undefined,
      });
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create the change');
    } finally {
      setBusy(false);
    }
  };

  const label: React.CSSProperties = { font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)', marginBottom: 5, display: 'block' };
  const input: React.CSSProperties = { border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', width: '100%', background: '#fff' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(54,54,54,0.32)', zIndex: 200 }} />
      <div ref={drawerRef} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, maxWidth: '100vw', background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--ew-n-100)', position: 'sticky', top: 0, background: '#fff', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ font: 'var(--ew-fw-semibold) 17px/1.3 var(--ew-font)' }}>New Change</div>
            <div style={{ fontSize: 12, color: 'var(--ew-n-500)', marginTop: 2 }}>Tracked here — the PM/PE enters accepted changes in Intacct</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ fontSize: 18, color: 'var(--ew-n-500)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rfi ? (
            <div style={{ padding: '8px 12px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12.5 }}>
              From <b>{rfi.rfiNumber}</b> — creating this PCO marks the RFI's change review complete.
            </div>
          ) : null}
          {error ? <div style={{ padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 }}>{error}</div> : null}

          {/* An answered RFI that carries a cost opens a POTENTIAL change by definition, and
              the API records the RFI's review as "pco" whatever this says, so there is no
              choice to offer. */}
          {rfi ? null : (
            <div>
              <span style={label}>Open it as</span>
              <select value={type} onChange={(e) => setType(e.target.value)} style={input}>
                {OPEN_AS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
          {type === 'COR' ? (
            <div>
              <span style={label}>Date sent to the client</span>
              <input type="date" value={sentOn} min={DATE_INPUT_MIN} max={easternTodayYmd()} onChange={(e) => setSentOn(e.target.value)} style={input} />
            </div>
          ) : null}
          {type === 'OCO' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <span style={label}>Date the client accepted</span>
                <input type="date" value={acceptedOn} min={DATE_INPUT_MIN} max={easternTodayYmd()} onChange={(e) => setAcceptedOn(e.target.value)} style={input} />
              </div>
              <div>
                <span style={label}>Client's reference</span>
                <input value={clientRef} onChange={(e) => setClientRef(e.target.value)} maxLength={120} style={input} placeholder="PO or signed change order" />
              </div>
            </div>
          ) : null}
          <div>
            <span style={label}>Description</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} style={input} placeholder="What changed and why" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <span style={label}>Amount ($, signed)</span>
              <input value={amount} onChange={(e) => setAmount(e.target.value)} style={input} type="number" placeholder="e.g. 12500 or -3000" />
            </div>
            <div>
              <span style={label}>Schedule impact (days)</span>
              <input value={scheduleDays} onChange={(e) => setScheduleDays(e.target.value)} style={input} type="number" placeholder="e.g. 3" />
            </div>
          </div>
          <div>
            <span style={label}>Cost code</span>
            <Combobox value={costCode} onChange={setCostCode} options={codeOptions} placeholder="Type to search cost codes…" />
          </div>
          <div>
            <span style={label}>Notes / justification</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...input, minHeight: 90, resize: 'vertical' }} placeholder="Origin (RFI, field condition, owner request), pricing basis, backup…" />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={submit} isLoading={busy}>Submit Change</Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Shared by the stage dialogs and the link dialog: backdrop, a focus trap
//     (Escape closes), and a labelled dialog. ──────────────────────────────────
function ModalShell({ title, intro, onClose, children }: {
  title: string;
  intro?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, { onEscape: onClose });
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(54,54,54,0.32)', zIndex: 200 }} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 520, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 'var(--ew-r-md)', zIndex: 201, boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: 20 }}
      >
        <div style={{ font: 'var(--ew-fw-semibold) 16px var(--ew-font)', marginBottom: 4 }}>{title}</div>
        {intro ? <div style={{ fontSize: 12.5, color: 'var(--ew-n-500)', marginBottom: 14 }}>{intro}</div> : null}
        {children}
      </div>
    </>
  );
}

const modalLabel: React.CSSProperties = { font: 'var(--ew-fw-medium) 11px/1 var(--ew-font)', letterSpacing: 'var(--ew-ls-caption)', textTransform: 'uppercase', color: 'var(--ew-n-500)' };
const modalInput: React.CSSProperties = { border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', font: 'var(--ew-fw-regular) 13px var(--ew-font)', background: '#fff', width: '100%' };
const modalError: React.CSSProperties = { marginBottom: 10, padding: '6px 10px', background: '#FBE4DF', color: '#8F2414', borderRadius: 'var(--ew-r-md)', fontSize: 12 };
const modalNote: React.CSSProperties = { marginBottom: 12, padding: '6px 10px', background: 'var(--ew-safety-50)', border: '1px solid var(--ew-safety-100)', borderRadius: 'var(--ew-r-md)', fontSize: 12, color: 'var(--ew-safety-700)' };

// ─── The stage buttons: sent to the client, client accepted, declined, correct
//     the accepted date, take Sage's new amount. One dialog, configured per kind,
//     so every one asks the same way and reports back the same way (onDone, which
//     the tab wires to invalidate). ───────────────────────────────────────────────
interface StageDialog {
  title: string;
  intro: React.ReactNode;
  dateLabel?: string;
  textLabel?: string;
  textPlaceholder?: string;
  textMax?: number;
  multiline?: boolean;
  confirm: string;
  danger?: boolean;
  failure: string;
  run: (v: { date: string; text: string }) => Promise<unknown>;
}

function stageDialog(kind: StageActionKind, c: ChangeOrder): StageDialog {
  switch (kind) {
    case 'send':
      return {
        title: `Sent ${c.changeNo} to the client`,
        intro: 'It becomes a COR, a change request with the client. Enter the day it went out.',
        dateLabel: 'Date sent',
        confirm: 'Mark as sent',
        failure: 'Failed to mark the change as sent',
        run: ({ date }) => changesApi.sentToClient(c.id, { sentOn: date }),
      };
    case 'accept':
      return {
        title: `Client accepted ${c.changeNo}`,
        intro: 'It becomes an OCO. The accepted date is the date it counts on, so enter the day the client accepted it, which may be earlier than today. Next you enter it in Sage Intacct and link it here.'
          + (stageOf(c) === 'PCO' ? ' This change was never marked as sent to the client, and it stays that way.' : ''),
        dateLabel: 'Date the client accepted',
        textLabel: "Client's reference (optional)",
        textPlaceholder: 'PO or signed change order number, or who emailed and when',
        textMax: 120,
        confirm: 'Mark as accepted',
        failure: 'Failed to record the acceptance',
        run: ({ date, text }) => changesApi.accept(c.id, { acceptedOn: date, clientRef: text || undefined }),
      };
    case 'decline': {
      // A PCO has not gone to the client, so there is nothing for them to decline: it is withdrawn.
      const withdrawing = stageOf(c) === 'PCO';
      return {
        title: withdrawing ? `Withdraw ${c.changeNo}` : `Decline or withdraw ${c.changeNo}`,
        intro: 'This ends the change. Any open crew requests raised against it are declined.',
        textLabel: 'Reason (optional)',
        textPlaceholder: withdrawing ? 'Why it is being withdrawn' : 'Why it was declined or withdrawn',
        textMax: 500,
        multiline: true,
        confirm: withdrawing ? 'Withdraw' : 'Decline',
        danger: true,
        failure: 'Failed to close the change',
        run: ({ text }) => changesApi.decline(c.id, { reason: text || undefined }),
      };
    }
    case 'correct':
      return {
        title: `Correct ${c.changeNo}`,
        intro: 'Fix the accepted date or the reference now. Once the change is linked to Sage Intacct they are locked.',
        dateLabel: 'Date the client accepted',
        textLabel: "Client's reference",
        textPlaceholder: 'PO or signed change order number',
        textMax: 120,
        confirm: 'Save',
        failure: 'Failed to save the correction',
        // Only what actually changed: the dialog opens with both fields filled in, and the
        // activity feed says what a correction touched. Nothing changed, nothing to send.
        run: ({ date, text }) => {
          const dateChanged = date !== (c.acceptedOn ?? '');
          const refChanged = text !== (c.clientRef ?? '');
          if (!dateChanged && !refChanged) return Promise.resolve();
          return changesApi.correctAcceptance(c.id, {
            acceptedOn: dateChanged ? date : undefined,
            clientRef: refChanged ? text : undefined,
          });
        },
      };
    case 'refresh':
      return {
        title: `Take Sage's new amount for ${c.changeNo}`,
        intro: c.countedAmount != null && c.intacctCoAmount != null
          ? `This change counts ${money(c.countedAmount)}, the Intacct amount when you linked it. Intacct now shows ${money(c.intacctCoAmount)}. Take the new amount if Intacct is right.`
          : 'Replace the counted amount with what Intacct shows now.',
        confirm: "Take Sage's amount",
        failure: "Failed to take Sage's amount",
        run: () => changesApi.refreshAmount(c.id),
      };
  }
}

function StageActionModal({ action, onClose, onDone }: {
  action: StageAction;
  onClose: () => void;
  onDone: () => void;
}) {
  const { kind, change } = action;
  const today = easternTodayYmd();
  const cfg = stageDialog(kind, change);
  const [date, setDate] = useState(kind === 'correct' ? (change.acceptedOn ?? today) : today);
  const [text, setText] = useState(kind === 'correct' ? (change.clientRef ?? '') : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A warning, never a block: a yes before the paperwork went out is real, but it is also the usual typo.
  const sentLater = kind === 'accept' ? acceptedBeforeSent(change, date) : null;

  const submit = async () => {
    if (cfg.dateLabel) {
      const bad = stageDateError(date);
      if (bad) { setError(`${cfg.dateLabel}: ${bad}`); return; }
    }
    setBusy(true); setError(null);
    try {
      await cfg.run({ date, text: text.trim() });
      onDone();
    } catch (e: any) {
      setError(e?.message ?? cfg.failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title={cfg.title} intro={cfg.intro} onClose={onClose}>
      {error ? <div style={modalError}>{error}</div> : null}
      {cfg.dateLabel ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
          <span style={modalLabel}>{cfg.dateLabel}</span>
          <input type="date" value={date} min={DATE_INPUT_MIN} max={today} onChange={(e) => setDate(e.target.value)} style={modalInput} />
          {sentLater ? (
            <span style={{ fontSize: 12, color: 'var(--ew-safety-700)' }}>
              That is before it was sent to the client ({fmtDate(sentLater)}). Fine if they said yes first; check it is not a typo.
            </span>
          ) : null}
        </label>
      ) : null}
      {cfg.textLabel ? (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
          <span style={modalLabel}>{cfg.textLabel}</span>
          {cfg.multiline ? (
            <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={cfg.textMax} placeholder={cfg.textPlaceholder} style={{ ...modalInput, minHeight: 70, resize: 'vertical' }} />
          ) : (
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={cfg.textMax} placeholder={cfg.textPlaceholder} style={modalInput} />
          )}
        </label>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant={cfg.danger ? 'danger' : 'primary'} onClick={submit} isLoading={busy}>{cfg.confirm}</Button>
      </div>
    </ModalShell>
  );
}

// ─── Link the Project Tracker change to a real Intacct CO. The confirmation can't be faked:
//     you can only pick a CO that actually exists on the job (synced from
//     PROJECTCHANGEORDER). Amount mismatch warns but doesn't block (legit
//     differences: tax, rounding, partial COs).
//
//     Two things on top of that. A CO within a dollar of the change's amount is
//     SUGGESTED (and preselected when it is the only one) — a suggestion, never a
//     link: the click is still the PM's. And the link FREEZES Intacct's amount as
//     the amount that counts, so the screen says so before it happens. ───────────
function IntacctEnteredModal({ change, projectId, onClose, onDone }: {
  change: ChangeOrder;
  projectId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const cosQuery = useQuery({ queryKey: ['intacct-cos', projectId], queryFn: () => changesApi.intacctCos(projectId) });
  // null = the PM has not touched the list, so a lone suggestion applies.
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cos = cosQuery.data ?? [];
  const suggestions = useMemo(() => suggestedSageMatches(change.amount, cos), [change.amount, cos]);
  const ordered = useMemo(() => [...suggestions, ...cos.filter((c) => !suggestions.includes(c))], [suggestions, cos]);
  const effectivePick = pick ?? defaultSagePick(change.amount, cos);
  const chosen = cos.find((c) => c.coId === effectivePick) ?? null;
  const amountDiffers = chosen?.amount != null && change.amount != null && Math.abs(chosen.amount - change.amount) >= SAGE_MATCH_TOLERANCE_DOLLARS;

  const submit = async () => {
    if (!effectivePick) { setError('Pick the Intacct CO this change was entered as.'); return; }
    setBusy(true); setError(null);
    try {
      await changesApi.intacctEntered(change.id, effectivePick);
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to confirm');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title="Link to the Intacct change order"
      intro={<>{change.changeNo} · {money(change.amount)} — pick the Sage Intacct CO you entered this as. Confirming links the two; every sync re-checks the link.</>}
      onClose={onClose}
    >
      {error ? <div style={modalError}>{error}</div> : null}

      {cosQuery.isLoading ? (
        <div style={{ fontSize: 13, color: 'var(--ew-n-500)', padding: '10px 0' }}>Loading this job's Intacct COs…</div>
      ) : cos.length === 0 ? (
        <div style={{ ...modalNote, color: 'var(--ew-n-600)', marginBottom: 14 }}>
          No Intacct change orders synced for this job. Enter the CO in Sage Intacct, run a sync, then link it here.
        </div>
      ) : (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
          <span style={modalLabel}>Intacct change order</span>
          <select value={effectivePick} onChange={(e) => setPick(e.target.value)} style={modalInput}>
            <option value="">— pick a CO —</option>
            {ordered.map((c) => (
              <option key={c.coId} value={c.coId} disabled={c.linked}>
                {c.coId} · {c.amount != null ? money(c.amount) : '—'}{c.description ? ` · ${c.description.slice(0, 40)}` : ''}{c.linked ? ' (already linked)' : ''}{suggestions.includes(c) ? ' (suggested match)' : ''}
              </option>
            ))}
          </select>
          {suggestions.length > 1 ? (
            <span className="chg-muted" style={{ fontSize: 11.5 }}>
              {suggestions.length} change orders are within a dollar of {money(change.amount)}. Check which one this is.
            </span>
          ) : null}
        </label>
      )}

      {amountDiffers ? (
        <div style={modalNote}>
          ⚠ Amounts differ — the Project Tracker has {money(change.amount)}, Intacct has {money(chosen!.amount)}. You can still link them (tax, rounding, or scope splits are common); the difference stays flagged for review.
        </div>
      ) : null}

      {chosen ? (
        <div className="chg-muted" style={{ fontSize: 12, marginBottom: 12 }}>
          {chosen.amount != null
            ? <>Linking saves Intacct's amount, {money(chosen.amount)}, as the amount that counts. If Intacct changes it later you will see a flag and decide whether to take the new amount.</>
            : <>Intacct shows no amount for this change order, so no counted amount is saved.</>}
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} isLoading={busy} disabled={!effectivePick}>Link &amp; confirm</Button>
      </div>
    </ModalShell>
  );
}
